import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { X, Mail, Loader2 } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Input,
  Button,
} from '@gu-corp/gu-ui';
import {
  registerClubEmail,
  verifyClubEmailCode,
  ClubEmailRequestError,
} from '~/services/joc-club-email';
import { getEnv } from '~/env';

interface Props {
  onClose: () => void;
  onVerified: () => void;
}

// Email registration gate shown before the SBT join flow (#619).
// Step 1: email + consent -> sends a Twilio Verify code.
// Step 2: 6-digit code -> marks the club_members record verified.
export const EmailRegistrationDialog: React.FC<Props> = ({ onClose, onVerified }) => {
  const { t, i18n } = useTranslation('common');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  // Terms-of-service agreement — required to proceed (email-preference opt-in is
  // covered by the terms and handled separately in settings).
  const [consent, setConsent] = useState(false);
  const termsUrl =
    (i18n.language === 'ja'
      ? getEnv('NEXT_PUBLIC_TERMS_OF_USE_JA')
      : getEnv('NEXT_PUBLIC_TERMS_OF_USE_EN')) || '#';
  const [code, setCode] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const errorMessage = (e: unknown): string => {
    if (e instanceof ClubEmailRequestError) {
      const known = [
        'invalid_email',
        'email_duplicated',
        'too_early_resend',
        'too_many_requests',
        'invalid_code',
        'not_registered',
      ];
      if (known.includes(e.code)) return t(`joc_club.email_error_${e.code}`);
    }
    return t('joc_club.email_error_unexpected');
  };

  const onSubmitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      await registerClubEmail(email.trim(), consent);
      setStep('code');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  };

  const onSubmitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      await verifyClubEmailCode(code.trim());
      onVerified();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <Card
        className="w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="email-registration-dialog"
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              {t('joc_club.email_dialog_title')}
            </CardTitle>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
              data-testid="email-dialog-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <CardDescription>
            {step === 'email'
              ? t('joc_club.email_dialog_desc')
              : t('joc_club.email_code_desc', { email })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === 'email' ? (
            <form onSubmit={onSubmitEmail} className="space-y-4">
              <Input
                type="email"
                required
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                placeholder={t('joc_club.email_placeholder')}
                data-testid="club-email-input"
              />
              <label className="flex items-start gap-2 text-xs text-muted-foreground">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5"
                  data-testid="club-email-consent"
                />
                <span>
                  {t('joc_club.email_terms_prefix')}
                  <a
                    href={termsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-foreground"
                  >
                    {t('terms_of_use')}
                  </a>
                  {t('joc_club.email_terms_suffix')}
                </span>
              </label>
              {error && <p className="text-xs text-red-500">{error}</p>}
              <Button
                type="submit"
                className="w-full"
                disabled={pending || !email.trim() || !consent}
                data-testid="club-email-submit"
              >
                {pending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t('joc_club.email_send_code')
                )}
              </Button>
            </form>
          ) : (
            <form onSubmit={onSubmitCode} className="space-y-4">
              <Input
                inputMode="numeric"
                required
                value={code}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value)}
                placeholder={t('joc_club.email_code_placeholder')}
                data-testid="club-email-code-input"
              />
              {error && <p className="text-xs text-red-500">{error}</p>}
              <Button
                type="submit"
                className="w-full"
                disabled={pending || !code.trim()}
                data-testid="club-email-verify"
              >
                {pending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t('joc_club.email_verify')
                )}
              </Button>
              <button
                type="button"
                onClick={() => {
                  setStep('email');
                  setError(null);
                }}
                className="w-full text-xs text-muted-foreground hover:text-foreground"
              >
                {t('joc_club.email_change')}
              </button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
